import { createContext, useContext, useState, useRef, useCallback, useMemo, type ReactNode } from "react";

// ============================================================================
// TYPES
// ============================================================================

/**
 * Configuration for the event debugger.
 */
export interface EventDebugConfig {
  /** Whether debug logging is enabled */
  enabled: boolean;
  /** Namespaces to capture (empty = all) */
  namespaces: string[];
  /** Event names to capture (empty = all) */
  events: string[];
  /** Whether to capture full argument snapshots (vs summary only) */
  captureArgs: boolean;
  /** Maximum number of log entries to retain */
  maxEntries: number;
}

/**
 * A single entry in the event log.
 */
export interface EventLogEntry {
  /** Unique incrementing ID for ordering */
  id: number;
  /** Timestamp when the event was emitted */
  timestamp: number;
  /** Namespace of the event bus that emitted */
  namespace: string;
  /** Name of the event */
  event: string;
  /** String summary of arguments (truncated) */
  argsSummary: string;
  /** Full argument snapshot (only if captureArgs is true) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  args?: any[];
  /** Number of handlers that received this event */
  handlerCount: number;
  /** Names of handlers that received this event */
  handlerNames: string[];
}

/**
 * Input for logging an event (before ID/timestamp are added).
 */
export interface EventLogInput {
  namespace: string;
  event: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  args: any[];
  handlerCount: number;
  handlerNames: string[];
}

/**
 * The debug context value provided to consumers.
 */
export interface EventDebugContextValue {
  /** Current configuration */
  config: EventDebugConfig;
  /** Whether debugging is currently enabled */
  enabled: boolean;
  /** Log entries (newest last) */
  entries: EventLogEntry[];
  /** Log an event (called by useEventListeners) */
  logEvent: (input: EventLogInput) => void;
  /** Clear all log entries */
  clearLog: () => void;
  /** Update configuration */
  setConfig: (updates: Partial<EventDebugConfig>) => void;
  /** Check if an event should be logged based on current filters */
  shouldLog: (namespace: string, event: string) => boolean;
}

// ============================================================================
// DEFAULTS
// ============================================================================

const DEFAULT_CONFIG: EventDebugConfig = {
  enabled: false,
  namespaces: [],
  events: [],
  captureArgs: false,
  maxEntries: 200,
};

// ============================================================================
// CONTEXT
// ============================================================================

/**
 * Context for event debugging. Returns null if not inside EventDebugProvider.
 */
export const EventDebugContext = createContext<EventDebugContextValue | null>(null);

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Generate a truncated string summary of arguments.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function summarizeArgs(args: any[], maxLength = 100): string {
  try {
    const json = JSON.stringify(args);
    if (json.length <= maxLength) return json;
    return json.slice(0, maxLength - 3) + "...";
  } catch {
    return "[unserializable]";
  }
}

/**
 * Deep clone arguments for snapshot (only when captureArgs is true).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function snapshotArgs(args: any[]): any[] {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return JSON.parse(JSON.stringify(args)) as any[];
  } catch {
    return ["[unserializable]"];
  }
}

// ============================================================================
// PROVIDER
// ============================================================================

interface EventDebugProviderProps {
  children: ReactNode;
  /** Initial configuration overrides */
  initialConfig?: Partial<EventDebugConfig>;
}

/**
 * Provider component for event debugging.
 * 
 * Wrap your app (or part of it) with this provider to enable event debugging.
 * The EventInspector component reads from this context to display the log.
 * 
 * @example
 * // In development only
 * {process.env.NODE_ENV === 'development' && (
 *   <EventDebugProvider initialConfig={{ maxEntries: 500 }}>
 *     <App />
 *     <EventInspector />
 *   </EventDebugProvider>
 * )}
 */
export function EventDebugProvider({ children, initialConfig }: EventDebugProviderProps) {
  // Configuration state
  const [config, setConfigState] = useState<EventDebugConfig>(() => ({
    ...DEFAULT_CONFIG,
    ...initialConfig,
  }));

  // Log entries stored in ref to avoid re-renders on every event
  // We use state for the "visible" entries that the UI reads
  const entriesRef = useRef<EventLogEntry[]>([]);
  const [entries, setEntries] = useState<EventLogEntry[]>([]);
  const nextIdRef = useRef(1);

  // Check if an event should be logged based on filters
  const shouldLog = useCallback((namespace: string, event: string): boolean => {
    if (!config.enabled) return false;
    
    // Check namespace filter
    if (config.namespaces.length > 0 && !config.namespaces.includes(namespace)) {
      return false;
    }
    
    // Check event filter
    if (config.events.length > 0 && !config.events.includes(event)) {
      return false;
    }
    
    return true;
  }, [config.enabled, config.namespaces, config.events]);

  // Log an event
  const logEvent = useCallback((input: EventLogInput) => {
    if (!shouldLog(input.namespace, input.event)) return;

    const entry: EventLogEntry = {
      id: nextIdRef.current++,
      timestamp: Date.now(),
      namespace: input.namespace,
      event: input.event,
      argsSummary: summarizeArgs(input.args),
      handlerCount: input.handlerCount,
      handlerNames: input.handlerNames,
    };

    // Add full args snapshot if configured
    if (config.captureArgs) {
      entry.args = snapshotArgs(input.args);
    }

    // Add to buffer, respecting max size
    entriesRef.current.push(entry);
    if (entriesRef.current.length > config.maxEntries) {
      entriesRef.current.shift();
    }

    // Update state for UI (batched by React)
    setEntries([...entriesRef.current]);
  }, [shouldLog, config.captureArgs, config.maxEntries]);

  // Clear all entries
  const clearLog = useCallback(() => {
    entriesRef.current = [];
    setEntries([]);
  }, []);

  // Update configuration
  const setConfig = useCallback((updates: Partial<EventDebugConfig>) => {
    setConfigState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Build context value
  const value = useMemo<EventDebugContextValue>(() => ({
    config,
    enabled: config.enabled,
    entries,
    logEvent,
    clearLog,
    setConfig,
    shouldLog,
  }), [config, entries, logEvent, clearLog, setConfig, shouldLog]);

  return (
    <EventDebugContext.Provider value={value}>
      {children}
    </EventDebugContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access the event debug context.
 * 
 * Returns null if not inside an EventDebugProvider (safe to use anywhere).
 * 
 * @example
 * const debug = useEventDebug();
 * if (debug?.enabled) {
 *   // debugging is active
 * }
 */
export function useEventDebug(): EventDebugContextValue | null {
  return useContext(EventDebugContext);
}

// ============================================================================
// PRODUCTION NO-OP
// ============================================================================

/**
 * A no-op provider for production builds.
 * Renders children without any debugging overhead.
 */
function NoOpProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

/**
 * Export the appropriate provider based on environment.
 * In production, this is a passthrough with zero overhead.
 */
export const EventDebugProviderAuto = 
  process.env.NODE_ENV === "development" ? EventDebugProvider : NoOpProvider;
